// Heurísticas de proximidade fatura ↔ movimento bancário, do lado do
// cliente. Extraídas de ModalCorrigirFatura.tsx quando o painel guiado
// (PainelConciliacao.tsx) passou a precisar exatamente das mesmas regras --
// duas cópias divergiriam com o tempo e o contabilista veria listas
// ordenadas de forma diferente conforme o sítio por onde entrasse.
//
// NUNCA decidem nada sozinhas: só ordenam/filtram o que é mostrado. Quem
// confirma é sempre o motor do backend (auto) ou um clique humano
// (ver src/core/reconciliation_engine.py e a convenção de nunca
// auto-confirmar estado de dinheiro).
import type { FaturaPendente, MovimentoExtrato } from "./types";

// Pontuação de proximidade ao movimento — mesmo espírito do motor de
// matching automático do backend (valor + data + texto), simplificado para
// ordenar a lista no cliente: a fatura mais parecida com o movimento
// aparece primeiro em vez de ficar perdida numa lista de dezenas de
// despesas por ordem arbitrária.
export function scoreProximidade(c: FaturaPendente, m: MovimentoExtrato): number {
  let score = 0;
  const fv = Math.abs(c.valor_total || 0);
  const mv = Math.abs(m.valor);
  if (fv > 0 && mv > 0) {
    const diff = Math.abs(fv - mv) / mv;
    if (diff < 0.001) score += 60;
    else if (diff < 0.01) score += 30;
    else if (diff < 0.05) score += 15;
    else if (diff < 0.10) score += 6;
  }
  const fd = c.data_documento ? new Date(c.data_documento).getTime() : NaN;
  const md = m.data_mov ? new Date(m.data_mov).getTime() : NaN;
  if (!Number.isNaN(fd) && !Number.isNaN(md)) {
    // Mesma regra do motor automático (calcular_score, reconciliation_engine.py):
    // um movimento nunca pode pagar uma fatura que ainda não existia --
    // pontuação de data fica sempre a 0 nesse caso, nunca "mais provável".
    if (md < fd) {
      score += 0;
    } else {
      const dias = (md - fd) / 86400000;
      if (dias === 0) score += 30;
      else if (dias <= 3) score += 24;
      else if (dias <= 7) score += 16;
      else if (dias <= 14) score += 8;
      else if (dias <= 30) score += 3;
    }
  }
  const nome = (c.nome_emitente || "").toLowerCase();
  const desc = (m.descricao || "").toLowerCase();
  if (nome && desc) {
    const palavras = nome.split(/\s+/).filter((w) => w.length > 3);
    if (palavras.length) {
      const hits = palavras.filter((p) => desc.includes(p)).length;
      score += 20 * (hits / palavras.length);
    }
  }
  return score;
}

// Impossível em qualquer circunstância, mesmo por pesquisa explícita: uma
// fatura não pode ser paga antes de existir. Ao contrário do resto de
// dentroDoIntervaloPlausivel (só afeta a lista por defeito, sem pesquisa),
// esta regra é absoluta -- pedido direto do dono do projeto depois de ver
// resultados de pesquisa a sugerir faturas fisicamente impossíveis para o
// movimento. Nunca mostrar, nem em modo de pesquisa.
export function dataImpossivel(c: FaturaPendente, m: MovimentoExtrato): boolean {
  const fd = c.data_documento ? new Date(c.data_documento).getTime() : NaN;
  const md = m.data_mov ? new Date(m.data_mov).getTime() : NaN;
  if (Number.isNaN(fd) || Number.isNaN(md)) return false;
  return md < fd;
}

// Candidato plausível por defeito (sem pesquisa ativa): mesmo mês ou no
// máximo 1 mês de diferença da data do movimento, e valor a menos de 20% de
// distância. Sem isto, a lista mostrava TODAS as faturas pendentes da
// empresa (podem ser dezenas, de meses completamente diferentes) só
// reordenadas por pontuação — o que na prática parecia uma lista de opções
// aleatórias e levou a uma conciliação errada numa demonstração real.
export function dentroDoIntervaloPlausivel(c: FaturaPendente, m: MovimentoExtrato): boolean {
  if (dataImpossivel(c, m)) return false;
  const fd = c.data_documento ? new Date(c.data_documento) : null;
  const md = m.data_mov ? new Date(m.data_mov) : null;
  if (fd && md && !Number.isNaN(fd.getTime()) && !Number.isNaN(md.getTime())) {
    const mesesDiff = Math.abs((md.getFullYear() - fd.getFullYear()) * 12 + (md.getMonth() - fd.getMonth()));
    if (mesesDiff > 1) return false;
  }
  const fv = Math.abs(c.valor_total || 0);
  const mv = Math.abs(m.valor);
  if (fv > 0 && mv > 0 && Math.abs(fv - mv) / mv > 0.20) return false;
  return true;
}

// Um movimento ainda precisa de decisão humana? É esta a fila por onde o
// painel guiado navega (ver PainelConciliacao) e a base do contador
// "faltam N de M" — tem de bater certo com o que a tabela mostra como
// pendente, senão o contador mente.
export function precisaDecisao(m: MovimentoExtrato): boolean {
  if (m.resolvido_sem_fatura) return false;
  if (m.match_estado === "confirmado") return false;
  return true; // 'pendente' (sugestão por rever) ou sem match nenhum
}

// Link de pré-visualização do Drive — aceita tanto /file/d/{id}/... como
// ?id={id}. Mesma conversão usada em App.js (openPDF) e ModalConfirmarMatch,
// só a nível de URL, para este módulo não depender do App.js legado.
export function drivePreviewUrl(link?: string | null): string | null {
  if (!link) return null;
  const m = link.match(/\/file\/d\/([^/?]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  const q = link.match(/[?&]id=([^&]+)/);
  if (q) return `https://drive.google.com/file/d/${q[1]}/preview`;
  return link;
}

// Abre o PDFModal global (montado uma vez na raiz do Dashboard, App.js) --
// mesmo CustomEvent "open-pdf" que App.js::openPDF dispara, para uma fatura
// candidata na lista do painel guiado poder ser vista sem sair do painel
// (z-index 4000 do PDFModal fica sempre por cima dos 3600 do Overlay deste
// painel). Só duplica o dispatch, não a lógica de conversão de URL (drivePreviewUrl).
export function openPdf(driveLink?: string | null, title = ""): void {
  const url = drivePreviewUrl(driveLink);
  if (!url) return;
  window.dispatchEvent(new CustomEvent("open-pdf", { detail: { url, title, original: driveLink } }));
}
